import styles from 'css/components/lesson-progress-modal.module.css'
import logoMark from 'img/logo-mark.png'

export type LessonProgressState = 'generating' | 'ready'

interface LessonProgressModalProps {
    state: LessonProgressState
    lessonName: string
}

/**
 * The blocking modal shown while a lesson's questions are being generated, and
 * briefly again once they are, before the redirect to the lesson builder.
 *
 * Deliberately NOT built on the shared `LoadingModal`, and deliberately has no
 * spinner. The customer's previous generating screen was a generic progress
 * circle plus "Please wait..." -- replaced on the customer's own instruction
 * because it told a teacher nothing. This design names the state ("Building
 * your lesson"), says what is actually happening ("Creating differentiated
 * pathways for X"), and gives a real expectation ("usually takes less than a
 * minute") instead of an indeterminate spinner.
 *
 * `LoadingModal` is left untouched: it still serves copy-to-class and
 * present-lesson, which are ordinary indeterminate waits where a spinner is
 * the right answer. Forcing both looks through one component would mean a
 * "loading" modal that also has to render a success state -- two jobs in one
 * brick. This is the second job, kept separate.
 *
 * Both states come from the same mockup pair, which differ only in copy.
 */
export const LessonProgressModal = ({ state, lessonName }: LessonProgressModalProps) => {
	const generating = state === 'generating'
	return (
		<div
			className={styles.modalOverlay}
			role="dialog"
			aria-modal="true"
			/* The copy swaps underneath a modal that never unmounts between the
			   two states, so a screen reader needs to be told it changed. */
			aria-live="polite"
		>
			<div className={styles.modalCard}>
				<div className={styles.modalMark}>
					{/* Decorative -- the heading below carries the meaning. */}
					<img src={logoMark} alt="" />
				</div>
				<span className={styles.modalEyebrow}>
					{generating ? 'Building your lesson' : 'Ready to go'}
				</span>
				<h2 className={styles.modalTitle}>
					{generating
						? 'Creating differentiated pathways for'
						: 'All three pathways are ready for'}
					<br />
					<span className={styles.modalLessonName}>"{lessonName}"</span>
				</h2>
				<p className={styles.modalFooterNote}>
					{generating
						? 'This usually takes less than a minute.'
						: 'Redirecting to your lesson…'}
				</p>
			</div>
		</div>
	)
}
